export type CsChatRole = 'user' | 'assistant' | 'system';

export interface CsChatMessage {
	/**
	 * Stable identity for the row. This is what lets the transcript's
	 * `@for (… ; track id)` reuse a row's DOM and component instance across
	 * streaming updates instead of tearing it down and rebuilding it on every
	 * delta — the prerequisite for the OnPush row optimisation.
	 */
	id: string;

	role: CsChatRole;
	content: string;
	timestamp?: Date;

	/** True while this message is still being streamed in (drives the live caret). */
	streaming?: boolean;

	/**
	 * Friendly model names that produced this message, in turn order
	 * (e.g. ['Haiku', 'Sonnet'] for a hybrid router→synthesis response).
	 * Rendered as small badges on assistant rows.
	 */
	models?: string[];

	/**
	 * What the assistant is doing RIGHT NOW (current tool call or model turn),
	 * shown with a live elapsed timer while the message streams. Transient —
	 * never persisted; cleared when the message completes.
	 */
	activity?: { label: string } | null;
}

let messageSequence = 0;

/**
 * Creates a {@link CsChatMessage} with a stable, unique id. Prefer this over an
 * object literal so every message gets a track-able identity.
 */
export function createCsChatMessage(init: Omit<CsChatMessage, 'id'> & { id?: string }): CsChatMessage {
	return { ...init, id: init.id ?? `cs-msg-${messageSequence++}` };
}
